// =============================================================
// Portal Bancos - Modelo de datos de la mesa hipotecaria (mock)
// Portado del proyecto SOZU Bank Ally.
// =============================================================

import type { NotaBanco } from "@/lib/portal-bancos/notas-banco";

// Identificador de banco asociado a una solicitud. Antes era un union de los 3
// bancos mock; ahora es el id (string) del banco con convenio real. La fuente de
// verdad de los bancos con convenio vive en `bancos_convenio` (ver hook
// `useBancosConvenio`); aquí solo se conserva el tipo para las solicitudes.
export type BankId = string;

export type IngresoRange = "15k-30k" | "30k-60k" | "60k-120k" | "120k+";
export type SituacionLaboral = "asalariado" | "honorarios" | "empresario" | "jubilado";

export const INGRESO_LABEL: Record<IngresoRange, string> = {
  "15k-30k": "$15,000 - $30,000",
  "30k-60k": "$30,000 - $60,000",
  "60k-120k": "$60,000 - $120,000",
  "120k+": "Más de $120,000",
};

export const SITUACION_LABEL: Record<SituacionLaboral, string> = {
  asalariado: "Asalariado con nómina formal",
  honorarios: "Profesionista independiente (honorarios)",
  empresario: "Empresario o accionista",
  jubilado: "Jubilado o pensionado",
};

export interface ConsentRecord {
  granted: boolean;
  timestamp: string;
  ley: "LFPDPPP";
}

export interface ClientData {
  fullName: string;
  phone: string;
  email: string;
  ingresoRange: IngresoRange;
  situacionLaboral: SituacionLaboral;
  esClienteActual: boolean;
  consent: ConsentRecord;
}

export interface CreditRequest {
  montoFinanciar: number;
  plazoAnios: 5 | 10 | 15 | 20;
  estMonthlyMin: number;
  estMonthlyMax: number;
  estRateMin: number;
  estRateMax: number;
  estCatMin: number;
  estCatMax: number;
}

export type ObraEtapa = "Cimentación" | "Estructura" | "Albañilería" | "Acabados" | "Terminado";

export interface PropertyData {
  project: string;
  unit: string;
  address: string;
  totalValue: number;
  saldoFinanciar: number;
  avanceObra: number;
  etapa: ObraEtapa;
  fechaEscrituracion: string;
}

export type LeadScore = "verde" | "amarillo" | "rojo";

export interface SozuMeta {
  leadId: string;
  score: LeadScore;
  declaredAt: string;
  agenteComercial: { name: string; phone: string };
}

// Pre-aprobado se retiró en oct-2026 (ticket #1245): de En evaluación el banco
// pasa directo a Crédito aprobado. `oferta_vinculante` conserva su id en la BD
// pero ahora es la etapa del Avalúo.
export type LeadStatus =
  | "nuevo" | "asignado" | "contactado" | "en_evaluacion"
  | "aprobado" | "oferta_vinculante" | "en_coordinacion"
  | "formalizado" | "rechazado" | "desistido" | "atendido_fuera";

export interface StatusDescriptor {
  label: string;
  tone: "neutral" | "info" | "warning" | "success" | "destructive";
  shortDesc: string;
  isTerminal: boolean;
  isWon: boolean;
}

export const STATUS_DESCRIPTORS: Record<LeadStatus, StatusDescriptor> = {
  nuevo:             { label: "Nuevo",              tone: "info",        shortDesc: "Lead recién recibido, sin asignar.",        isTerminal: false, isWon: false },
  asignado:          { label: "Asignado",           tone: "info",        shortDesc: "Asignado a un ejecutivo.",                  isTerminal: false, isWon: false },
  contactado:        { label: "Contactado",         tone: "info",        shortDesc: "Primer contacto realizado.",                isTerminal: false, isWon: false },
  en_evaluacion:     { label: "En evaluación",      tone: "info",        shortDesc: "Recabando documentación y evaluando.",      isTerminal: false, isWon: false },
  // Crédito aprobado: el banco autorizó el crédito tras el análisis y sube la
  // carta de autorización. Las condiciones todavía dependen del avalúo.
  aprobado:          { label: "Crédito aprobado",   tone: "success",     shortDesc: "El banco autorizó el crédito del cliente.", isTerminal: false, isWon: false },
  // Avalúo: el id sigue siendo `oferta_vinculante` en la BD. El crédito ya
  // cuenta como aprobado; el banco valúa el inmueble.
  oferta_vinculante: { label: "Avalúo",             tone: "success",     shortDesc: "El banco está haciendo el avalúo del inmueble.", isTerminal: false, isWon: false },
  en_coordinacion:   { label: "Coordinando notaría",tone: "info",        shortDesc: "Alineando notario, fecha y pago.",          isTerminal: false, isWon: false },
  formalizado:       { label: "Formalizado",        tone: "success",     shortDesc: "Crédito formalizado, listo para escriturar.", isTerminal: true,  isWon: true  },
  rechazado:         { label: "Rechazado",          tone: "destructive", shortDesc: "El banco declinó la solicitud.",            isTerminal: true,  isWon: false },
  desistido:         { label: "Desistido",          tone: "neutral",     shortDesc: "El cliente no avanzó con esta institución.",isTerminal: true,  isWon: false },
  // El cliente sigue con ESTE banco, pero lo lleva otro ejecutivo por fuera de
  // SOZU (sucursal, su ejecutivo de siempre). No es pérdida ni desistimiento:
  // "desistido" le diría al cliente que elija otro banco, y no es verdad.
  atendido_fuera:    { label: "Atendido por fuera", tone: "neutral",     shortDesc: "Lo tramita otro ejecutivo del banco, fuera de la app.", isTerminal: true, isWon: false },
};

export const PIPELINE_ORDER: LeadStatus[] = [
  "nuevo", "asignado", "contactado", "en_evaluacion",
  "aprobado", "oferta_vinculante", "en_coordinacion", "formalizado",
];

/**
 * Etapas en las que el crédito ya cuenta como aprobado en todo el sistema
 * (app del cliente, notaría, VoBo del banco, Liquidadas): de Crédito aprobado en
 * adelante, Avalúo incluido. Espejo de la función SQL
 * `bancos_solicitud_credito_aprobado(text)`.
 */
export const ETAPAS_CREDITO_APROBADO: LeadStatus[] = [
  "aprobado", "oferta_vinculante", "en_coordinacion", "formalizado",
];

/** ¿El banco ya aprobó el crédito en esta etapa? Acepta el texto crudo de la BD. */
export function esCreditoAprobado(status: string | null | undefined): boolean {
  return status != null && (ETAPAS_CREDITO_APROBADO as string[]).includes(status);
}

/**
 * Traduce la etapa retirada `pre_aprobado` a En evaluación; lo demás pasa igual.
 * Hasta que corra la migración que mueve esas filas, la BD todavía puede
 * traerla, y sin traducir caería a "Nuevo" en la mesa y sin texto en la app del
 * cliente. Acepta el texto crudo de la BD.
 */
export function etapaVigente<T extends string | null | undefined>(estatus: T): T | "en_evaluacion" {
  return estatus === "pre_aprobado" ? "en_evaluacion" : estatus;
}

export const VALID_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  nuevo: ["asignado", "contactado", "desistido", "rechazado", "atendido_fuera"],
  asignado: ["contactado", "nuevo", "desistido", "rechazado", "atendido_fuera"],
  contactado: ["en_evaluacion", "asignado", "desistido", "rechazado", "atendido_fuera"],
  en_evaluacion: ["aprobado", "contactado", "desistido", "rechazado", "atendido_fuera"],
  aprobado: ["oferta_vinculante", "en_evaluacion", "desistido", "rechazado", "atendido_fuera"],
  oferta_vinculante: ["en_coordinacion", "aprobado", "desistido", "rechazado", "atendido_fuera"],
  en_coordinacion: ["formalizado", "oferta_vinculante", "desistido", "rechazado", "atendido_fuera"],
  formalizado: [],
  rechazado: [],
  desistido: [],
  atendido_fuera: [],
};

export function canTransition(from: LeadStatus, to: LeadStatus): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Cambios de estado que cierran el caso o son difíciles de revertir: la UI pide
 * confirmación explícita antes de aplicarlos (un clic de más dejaba la
 * solicitud rechazada sin vuelta atrás).
 */
export const CONFIRM_REQUIRED_STATUSES: LeadStatus[] = [
  "rechazado", "desistido", "atendido_fuera", "formalizado",
];

/** Cierres que exigen capturar un motivo antes de confirmar. */
export const REASON_REQUIRED_STATUSES: LeadStatus[] = ["rechazado", "desistido", "atendido_fuera"];

/** Cierres fuera del embudo (no son un paso del pipeline). */
export const CIERRES_FUERA_DEL_EMBUDO: LeadStatus[] = ["rechazado", "desistido", "atendido_fuera"];

/**
 * Reapertura de un cierre equivocado. No vive en `VALID_TRANSITIONS` a
 * propósito: no es un avance normal del pipeline sino una corrección. La hace
 * el Operador, el Admin del banco o el Super Administrador, y pide
 * confirmación + motivo, que queda en la Bitácora con quién la hizo.
 */
export const REOPEN_TARGETS: Partial<Record<LeadStatus, LeadStatus[]>> = {
  rechazado: ["contactado", "en_evaluacion"],
  desistido: ["contactado", "en_evaluacion"],
  // Si el cliente regresa a la app (o se marcó por error).
  atendido_fuera: ["contactado", "en_evaluacion"],
};

export function canReopen(status: LeadStatus): boolean {
  return (REOPEN_TARGETS[status]?.length ?? 0) > 0;
}

export const REJECTION_REASONS = [
  "Capacidad de pago insuficiente",
  "Historial crediticio (Buró)",
  "Documentación incompleta",
  "Avalúo por debajo del monto",
  "Cliente fuera de política",
  "Otro",
] as const;

export const DESIST_REASONS = [
  "Eligió otro banco",
  "Decidió pagar de contado",
  "No respondió",
  "Pospuso la compra",
  "Otro",
] as const;

export const FUERA_REASONS = [
  "Lo atiende otro ejecutivo del banco",
  "Lo tramitó directo en sucursal",
  "Ya tenía crédito en trámite con el banco",
  "Otro",
] as const;

export type LeadHealth = "en_tiempo" | "en_riesgo" | "detenido";

export function daysUntil(iso: string): number {
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

const EARLY_STATUSES: LeadStatus[] = ["nuevo", "asignado", "contactado", "en_evaluacion"];

export function deriveHealth(lead: { status: LeadStatus; property: PropertyData; lastUpdate: string }): LeadHealth {
  const desc = STATUS_DESCRIPTORS[lead.status];
  if (desc.isTerminal) return "en_tiempo";
  const dias = daysUntil(lead.property.fechaEscrituracion);
  const diasSinUpdate = daysUntil(lead.lastUpdate) * -1;
  if (diasSinUpdate >= 5) return "detenido";
  if (dias < 30 && EARLY_STATUSES.includes(lead.status)) return "en_riesgo";
  if (dias < 15) return "en_riesgo";
  return "en_tiempo";
}

export const HEALTH_DESCRIPTOR: Record<LeadHealth, { label: string; tone: "success" | "warning" | "destructive" }> = {
  en_tiempo: { label: "En tiempo", tone: "success" },
  en_riesgo: { label: "En riesgo", tone: "warning" },
  detenido:  { label: "Detenido",  tone: "destructive" },
};

export interface ActivityEntry {
  id: string;
  ts: string;
  author: string;
  type: "creado" | "status_change" | "nota" | "contacto";
  from?: LeadStatus;
  to?: LeadStatus;
  note?: string;
}

/**
 * Datos comerciales reales de la venta asociada a la solicitud (cuenta de
 * cobranza). Se muestran en el detalle de la Bandeja para que el banco conozca
 * el avance de pago del cliente. Opcional: si la solicitud no resuelve una
 * cuenta de cobranza (o la BD no está disponible), queda `undefined` y la UI
 * degrada sin romperse.
 */
export interface SaleData {
  /** cuentas_cobranza.fecha_compra */
  fechaVenta: string | null;
  /** cuentas_cobranza.precio_final */
  valorEscrituracion: number;
  /** Σ aplicaciones_pago.monto WHERE es_multa = false (vía acuerdos de la cuenta) */
  totalPagado: number;
  /** valorEscrituracion − totalPagado (mínimo 0) */
  saldoPendiente: number;
  /** Copropietarios de la cuenta (tabla `compradores`). */
  compradores: Array<{ idPersona: number; nombre: string; porcentaje: number }>;
}

/**
 * Por dónde llegó la solicitud (`bancos_solicitudes.canal`).
 * - directo: el cliente eligió el banco en su app (flujo hasta oct-2026).
 * - broker: llegó al Broker Bancario, que la deriva a UN banco a la vez.
 * - sozu: el cliente eligió un banco sin convenio; la atiende SOZU (solo la ve el Super Administrador).
 */
export type CanalSolicitud = "directo" | "broker" | "sozu";

/** Lo que se muestra mientras el broker no deriva la solicitud a un banco. */
export const NOMBRE_SIN_BANCO = "Sin derivar";

/**
 * Etapas que solo existen con un banco: las decide el banco (o la operación con
 * él). Una solicitud del broker sin derivar no puede llegar aquí; la BD lo
 * impide con `bancos_solicitudes_banco_requerido_check`.
 */
export const ETAPAS_REQUIEREN_BANCO: LeadStatus[] = [
  "aprobado", "oferta_vinculante", "en_coordinacion", "formalizado", "atendido_fuera",
];

/** ¿Se puede pasar la solicitud a `to` con el banco que tiene hoy? */
export function etapaPermitidaSinBanco(lead: { bankId: BankId }, to: LeadStatus): boolean {
  return !!lead.bankId || !ETAPAS_REQUIEREN_BANCO.includes(to);
}

export interface BankLead {
  id: string;
  /** Id (string) del banco; "" = solicitud del broker aún sin derivar. */
  bankId: BankId;
  /** Nombre del banco (catálogo `bancos`); null si no tiene banco. */
  bankName?: string | null;
  canal: CanalSolicitud;
  /** Cuándo el broker la mandó al banco (`fecha_derivacion`). */
  fechaDerivacion?: string | null;
  /** Email de quien la derivó (`derivada_por`). */
  derivadaPor?: string | null;
  /** Solicitud previa de la misma cuenta cuando el broker cambió de banco. */
  idSolicitudAnterior?: number | null;
  status: LeadStatus;
  assignedAgentId?: string;
  closeReason?: string;
  /** `cuentas_cobranza.tipo_financiamiento`: forma de pago final que hoy tiene la cuenta. */
  formaPagoCuenta?: string | null;
  /** `fecha_respuesta_banco`: cuándo dictaminó el banco. */
  fechaAprobacion?: string | null;
  /** `fecha_expiracion`: hasta cuándo sostiene el banco esa aprobación. */
  fechaExpiracion?: string | null;
  client: ClientData;
  credit: CreditRequest;
  property: PropertyData;
  sozu: SozuMeta;
  sale?: SaleData;
  /** Cuenta de cobranza asociada a la solicitud - clave para el detalle de
   *  cliente(s) (validación / documentos). `null` si la solicitud no resuelve. */
  idCuentaCobranza?: number | null;
  /** Cliente(s) de la solicitud para el visor de datos personales (solo vista).
   *  Prefiere los copropietarios de `compradores`; si no hay, usa la persona-lead. */
  clientes?: Array<{ idPersona: number; nombre: string }>;
  activity: ActivityEntry[];
  /**
   * Notas de la Bitácora (`notas_banco`): las del banco o del broker y los
   * registros de sistema (cambios de etapa, carta, vigencia, asignación). Aquí son
   * solo lectura; se escriben con `useCrearNota`. Opcional: no todo origen las trae.
   */
  notas?: NotaBanco[];
  createdAt: string;
  lastUpdate: string;
}

// Montos SIEMPRE con centavos: el banco cruza estas cifras contra el estado de
// cuenta del cliente, y redondear a pesos dejaba $1,771,180 donde la app dice
// $1,771,180.54.
const MXN = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
export const fmtMXN = (n: number) => MXN.format(n);

export const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(iso));

export const plural = (n: number, singular: string, pluralForm: string) =>
  `${n} ${n === 1 ? singular : pluralForm}`;

export function closedDescriptor(
  status: LeadStatus
): { label: string; tone: "success" | "destructive" | "neutral" } | null {
  switch (status) {
    case "formalizado": return { label: "Cerrado - ganado", tone: "success" };
    case "rechazado":   return { label: "Rechazado",        tone: "destructive" };
    case "desistido":   return { label: "No avanzó",        tone: "neutral" };
    default: return null;
  }
}

export function amortMonthly(principal: number, annualRatePct: number, years: number): number {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}